import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { debounce } from 'lodash';
import { produce } from 'immer';
import {
  getProject,
  getProjectVersion,
  cloneProject,
  updateProject,
  updateProjectVisibility,
} from '../../../services/ProjectService';
import { normalizeVersionSnapshot } from '../utils/normalizeVersionSnapshot';
import { resumeCreateStream, resolvePendingImages } from '../../../services/UnlimitedService';
import {
  createLoadingBlock,
  cleanLoadingObjectsFromPacket,
} from '../../Packet/Contexts/packetContextUtils';
import ProjectChangesStack from '../utils/ProjectChangesStack';
import {
  READY_CHANGED,
  READY_NEW,
  readyKind,
  tabMarkLabel,
  useReadyResourceTabs,
} from '../utils/readyResourceTabs';
import { Mixpanel } from '../../../services/Mixpanel';
import { isProjectQuotaExceeded, withProjectCounted } from '../../../utils/isProjectQuotaExceeded';
import { useDiffitUserContext } from '../../../contexts/DiffitUserContext';
import {
  applyImageOverlayToDraft,
  harvestResolvedImageSlots,
  hasPendingImages,
} from '../../Packet/Blocks/CustomActivityV2/imageSlots';
import { mergePacketsPreservingLocal } from '../utils/mergeStreamingSnapshot';
import {
  deriveStreamSession,
  latchStreamSession,
  packetUrlWithParams,
  withoutStreamParams,
} from '../utils/streamSession';

// Debounce delay for saving project updates to server (matches PacketContext pattern)
const SAVE_DEBOUNCE_MS = 1000;

// The only packet fields the editor can mutate — mirrors the allowlist the clone
// endpoint enforces. Cleaned the same way the owner autosave path cleans before a
// PUT, so a carried edit is byte-identical to what a normal save would have written.
const pickCloneOverrideFields = (packet) => {
  const { blocks, title, settings, input_flags_json } = cleanLoadingObjectsFromPacket(packet);
  return { blocks, title, settings, input_flags_json };
};

// Whether every outline activity of the streamed primary has landed. Read by both
// the loading-placeholder padding and the generation registry so the two can't
// disagree. totalActivities === 0 means the count never arrived (streamSession
// defaults a missing param to 0), NOT "nothing to generate" — so it is never
// complete until allDone.
const isPrimaryGenerationComplete = (packet, totalActivities, allDone) => {
  const indexedCount = (packet?.blocks || []).filter((b) => b._activity_index != null).length;
  return allDone || (totalActivities > 0 && indexedCount >= totalActivities);
};

const withId = (ids, id) => (!id || ids.has(id) ? ids : new Set(ids).add(id));
const withoutId = (ids, id) => {
  if (!ids.has(id)) return ids;
  const next = new Set(ids);
  next.delete(id);
  return next;
};
const EMPTY_IDS = new Set();

// The default value is what a consumer rendered outside ProjectProvider gets —
// the `!context` guard below can't catch that case. getHasOwner is stubbed to
// true so a stray consumer reads "owned" rather than undefined, which would
// otherwise show the "Sign in to save this resource" banner on every packet.
export const ProjectContext = createContext({ getHasOwner: () => true });

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { projectId, packetId: urlPacketId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSignedIn } = useAuth();
  const { setDiffitUser } = useDiffitUserContext();

  // Project state
  const [project, setProject] = useState(null);
  const [packets, setPackets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  // Assume owned until the fetch says otherwise — defaulting to false paints the
  // "sign in to save this" banner over every project for the length of the load.
  const [hasOwner, setHasOwner] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [isCloning, setIsCloning] = useState(false);
  const [showSavedSuccess, setShowSavedSuccess] = useState(false);

  // Active packet state - derived from URL or first packet
  const [activePacketId, setActivePacketId] = useState(urlPacketId || null);

  // Chat state (will be extended in Phase 7)
  const [chatOpen, setChatOpen] = useState(false);
  const [pendingChatInput, setPendingChatInput] = useState('');

  // Streaming create state — initialize from the URL so the very first render knows
  // we're streaming (prevents a skeleton flash before the effect fires). Via
  // deriveStreamSession rather than a raw param read, so this agrees with the rest
  // of the file on what counts as a stream: ?stream_id alone, or alongside ?version,
  // isn't a resumable session, and starting true there means one commit of wrong
  // state before the effect below corrects it.
  const [isStreaming, setIsStreaming] = useState(() => !!deriveStreamSession(searchParams));
  const [outlineState, setOutlineState] = useState(null);
  // Packet ids still being generated by the streaming create. Tracked by id from
  // the stream lifecycle rather than read off the blocks, because an ordinary
  // edit strips the loading placeholders mid-generation (cleanLoadingObjectsFromPacket).
  const [generatingPacketIds, setGeneratingPacketIds] = useState(EMPTY_IDS);

  // The stream session is LATCHED off the URL, not read from it reactively. The
  // query string churns for reasons unrelated to streaming (tab switch, version
  // exit, multi-export OAuth return), and a churn that dropped stream_id used to
  // look exactly like "the stream ended" — the resume effect aborted its SSE
  // connection and its re-run had nothing left to reconnect to, so every
  // remaining activity and image event for the run was lost. See streamSession.js.
  //
  // Adjusted during render (React's derived-state pattern) rather than in an
  // effect, so streamSession is never stale for a render and a discarded render
  // can't drop the latch. Converges in one step: latchStreamSession returns the
  // same object once there's nothing left to change.
  const [streamLatch, setStreamLatch] = useState(() => ({
    projectId,
    session: deriveStreamSession(searchParams),
  }));
  const nextStreamLatch = latchStreamSession(
    streamLatch,
    deriveStreamSession(searchParams),
    projectId
  );
  if (nextStreamLatch !== streamLatch) setStreamLatch(nextStreamLatch);
  const streamSession = nextStreamLatch.session;

  // Track which tabs have unseen updates (new or modified while not active)
  const [updatedPacketIds, setUpdatedPacketIds] = useState(new Set());
  // Tabs whose new work arrived in this browser lately, until they're opened: the Ready pill
  // (new) or the dot (changed).
  const [readyTabs, markTabsReady, forgetTabsReady] = useReadyResourceTabs(activePacketId);

  // Version snapshot state
  const [versionMeta, setVersionMeta] = useState(null);
  const [versionUnavailable, setVersionUnavailable] = useState(false);

  // Remove resource confirmation modal state
  // Shape: { packetId, packetTitle, onConfirm } or null
  const [removeResourceConfirmation, setRemoveResourceConfirmation] = useState(null);

  // Project-level undo/redo stack.
  const undoRedoStack = useRef(null);

  // Track if initial load has completed
  const initialLoadDoneRef = useRef(false);

  // Ref to track activePacketId for use in refreshProject without adding it as a dependency
  // (adding activePacketId to refreshProject's deps would cascade recreations through chat hooks)
  const activePacketIdRef = useRef(activePacketId);

  // Same reason as above, for the navigations that must carry the query string
  // forward: putting searchParams in switchToPacket's deps would cascade
  // recreations through the chat hooks on every param change.
  //
  // The setter needs a ref for a sharper reason: useSearchParams re-creates it
  // whenever searchParams changes, so listing it in the streaming effect's deps
  // would churn that effect on every param change — exactly what latching the
  // session is meant to prevent. finishStream reads it through the ref instead.
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);

  // Deferred-image streaming: image_id -> { status: 'ready'|'failed', images }.
  // image_ready/image_error events patch blocks in memory by image_id; this overlay
  // is re-applied on every streaming refetch so a stale snapshot (taken before the
  // image committed) can never revert a resolved slot back to a pending skeleton.
  const resolvedImagesRef = useRef(new Map());

  // Packets we've already asked the backfill endpoint to resolve, so the on-load
  // self-heal fires at most once per packet per mount (deduped by id).
  const backfilledPacketIdsRef = useRef(new Set());

  useEffect(() => {
    activePacketIdRef.current = activePacketId;
  }, [activePacketId]);

  // Layout effect, not passive: switchToPacket reads this ref, and a caller firing
  // from its own layout effect in the same commit would otherwise navigate with the
  // previous query string.
  useLayoutEffect(() => {
    searchParamsRef.current = searchParams;
    setSearchParamsRef.current = setSearchParams;
  }, [searchParams, setSearchParams]);

  // Initialize the project-level undo/redo stack once the project id is known.
  // Do NOT push a baseline — ProjectPacketContext owns push timing.
  useEffect(() => {
    if (projectId) {
      undoRedoStack.current = new ProjectChangesStack(projectId);
    }
  }, [projectId]);

  const versionId = searchParams.get('version');

  // Load project data - only depends on projectId and versionId, NOT urlPacketId
  // This ensures tab switching is instant (client-side) without reloading from API
  const loadProject = useCallback(async () => {
    if (!projectId) return;

    try {
      setIsLoading(true);

      if (!versionId) {
        setVersionMeta(null);
        setVersionUnavailable(false);
      }

      if (versionId) {
        const streamId = searchParams.get('stream_id');
        if (streamId) {
          console.warn(
            '[ProjectContext] Both ?version= and ?stream_id= are present; ignoring stream_id.'
          );
        }

        const response = await getProjectVersion(projectId, versionId);
        const normalized = normalizeVersionSnapshot(response.data, versionId);

        setProject(normalized.project);
        setPackets(normalized.packets);
        setIsOwner(normalized.is_owner);
        setHasOwner(normalized.has_owner);
        setCanEdit(normalized.can_edit);
        setVersionMeta(normalized.versionMeta);
        setVersionUnavailable(false);
        setIsLoading(false);
        initialLoadDoneRef.current = true;
        return;
      }

      const response = await getProject(projectId);
      const data = response.data;

      setProject(data.project);
      setPackets(data.packets || []);
      setIsOwner(data.is_owner);
      setHasOwner(data.has_owner);
      setCanEdit(data.can_edit);
      setIsLoading(false);
      initialLoadDoneRef.current = true;
    } catch (error) {
      if (versionId && error.response?.status === 403) {
        setVersionUnavailable(true);
        setIsLoading(false);
        return;
      }
      console.error('Failed to load project:', error);
      setIsLoading(false);
    }
  }, [projectId, versionId]); // Note: NO urlPacketId dependency - this is intentional

  // Initial load - when projectId or versionId changes
  // Skip only when the streaming effect will really take over. Gating on a raw
  // stream_id read meant a URL with stream_id but no last_event_id skipped this AND
  // bailed out of the streaming effect, so the project never loaded at all.
  // deriveStreamSession also encodes the version-mode exemption.
  useEffect(() => {
    initialLoadDoneRef.current = false;
    if (deriveStreamSession(searchParams)) return;
    loadProject();
  }, [loadProject, versionId]);

  // Handle URL packet ID changes and initial redirect
  // This runs AFTER project loads and handles:
  // 1. Initial redirect to first packet if no packet in URL
  // 2. Sync activePacketId when URL changes (tab switch)
  useEffect(() => {
    // Wait for initial load to complete
    if (!initialLoadDoneRef.current || packets.length === 0) return;

    if (!urlPacketId) {
      // No packet in URL - redirect to first packet, preserving any search params
      const firstPacketId = packets[0]?.packet?.id;
      if (firstPacketId) {
        navigate(packetUrlWithParams(projectId, firstPacketId, searchParams), { replace: true });
        setActivePacketId(firstPacketId);
      }
    } else if (urlPacketId !== activePacketId) {
      // URL changed (tab switch) - update active packet state only, NO API call
      setActivePacketId(urlPacketId);
    }
  }, [urlPacketId, packets, projectId, navigate, activePacketId, searchParams]);

  // Resume a streaming create session (early redirect from Unlimited page)
  useEffect(() => {
    // No session for this project — we never had one, the URL carried a malformed
    // pair, or we walked into a different project mid-generation. Clear the flag
    // rather than leaving it stuck true: it gates the pending-image backfill below
    // and drives the loading-placeholder rendering.
    if (!streamSession || !projectId) {
      setIsStreaming(false);
      setGeneratingPacketIds(EMPTY_IDS);
      // Match the per-stream lifetime documented at the reset below: without this the
      // map outlives the project and accumulates for the whole SPA session.
      resolvedImagesRef.current = new Map();
      return;
    }
    const { streamId, lastEventId, totalActivities } = streamSession;

    // Capture the packet ID that is being streamed. This must NOT change if the
    // user switches tabs during streaming — loading blocks and retry logic always
    // target the original packet, not whatever tab happens to be active.
    const streamingPacketId = urlPacketId;

    setIsStreaming(true);
    setGeneratingPacketIds(withId(EMPTY_IDS, streamingPacketId));
    // Fresh per-stream overlay of images resolved via image_ready (see below).
    resolvedImagesRef.current = new Map();
    const abortController = new AbortController();

    // Derive activity names and secondary declarations from the create outline for
    // loading-block / placeholder-tab injection below.
    let activityNames = [];
    let secondaryDeclarations = [];
    const applyOutline = (outline) => {
      if (!outline || !outline.packets) return;
      setOutlineState(outline);
      const primary = outline.packets.find((p) => p.role === 'primary');
      activityNames = primary?.activities || [];
      // Translations / relevels always share the primary's orientation; companions
      // can differ, so use each companion's own orientation from the outline.
      const versionEntries = (primary?.versions || []).map((v) => ({
        packet_index: v.packet_index,
        type: v.role === 'releveled' ? 'relevel' : v.role,
        language: v.language,
        title: v.title,
        orientation: primary?.orientation,
      }));
      const companionEntries = (outline.packets || [])
        .filter((p) => p.role === 'companion')
        .map((c) => ({
          packet_index: c.packet_index,
          type: 'companion',
          title: c.title,
          orientation: c.orientation || primary?.orientation,
        }));
      secondaryDeclarations = [...versionEntries, ...companionEntries].sort(
        (a, b) => a.packet_index - b.packet_index
      );
    };

    // Prefer the outline persisted at create time (sessionStorage). When the project is
    // opened fresh — e.g. the MCP connector's "Open in Diffit" link — sessionStorage is
    // empty; we then fall back to the outline returned on the project response, populated
    // inside refreshWithLoadingBlocks below.
    try {
      const storedOutline = sessionStorage.getItem(`stream_outline_${streamId}`);
      if (storedOutline) applyOutline(JSON.parse(storedOutline));
    } catch (e) {
      // malformed/unavailable — ignore; the project-response fallback covers it
    }

    // Generation counters — prevent stale getProject() responses from overwriting fresher data.
    // Separate counters for primary and secondary refreshes so secondary-only calls don't
    // invalidate in-flight primary fetches (and vice versa).
    let primaryFetchGeneration = 0;
    let secondaryFetchGeneration = 0;

    // Once generation has fully settled, rebuild the outline from the ACTUAL packets
    // in the project (activities can be dropped on error, merged, or renamed during
    // generation). Packets that are missing or have no blocks are dropped entirely —
    // the finished outline must list only what was really generated.
    const rebuildOutlineFromPackets = (freshPackets) => {
      setOutlineState((prev) => {
        if (!prev?.packets?.length) return prev;

        const toActivities = (blocks, planned) => {
          const plannedNames = new Map((planned || []).map((a) => [a.index, a.name]));
          return blocks.map((b, i) => ({
            index: i,
            name:
              b.template_data?.title?.text ||
              plannedNames.get(b._activity_index) ||
              `Activity ${i + 1}`,
            status: 'ready',
          }));
        };

        // Secondaries appear after the primary in declaration order
        // (same positional assumption as placeholder-tab injection below).
        const secondariesFresh = freshPackets.filter((p) => p.packet.id !== streamingPacketId);
        const prevPrimary = prev.packets.find((p) => p.role === 'primary');
        const declaredSlots = [
          ...(prevPrimary?.versions || []).map((v) => ({ kind: 'version', decl: v })),
          ...prev.packets
            .filter((p) => p.role === 'companion')
            .map((c) => ({ kind: 'companion', decl: c })),
        ].sort((a, b) => a.decl.packet_index - b.decl.packet_index);

        const versions = [];
        const companions = [];
        declaredSlots.forEach((slot, i) => {
          const fresh = secondariesFresh[i];
          // Missing or empty (failed / never created) — drop from the outline.
          if (!fresh?.packet?.blocks?.length) return;
          if (slot.kind === 'version') {
            versions.push({
              ...slot.decl,
              title: fresh.packet.title || slot.decl.title,
              status: 'ready',
            });
          } else {
            companions.push({
              ...slot.decl,
              title: fresh.packet.title || slot.decl.title,
              activities: toActivities(fresh.packet.blocks, slot.decl.activities),
            });
          }
        });

        const rebuilt = [];
        const freshPrimary = freshPackets.find((p) => p.packet.id === streamingPacketId);
        if (prevPrimary && freshPrimary?.packet?.blocks?.length) {
          rebuilt.push({
            ...prevPrimary,
            title: freshPrimary.packet.title || prevPrimary.title,
            activities: toActivities(freshPrimary.packet.blocks, prevPrimary.activities),
            versions,
          });
        }
        rebuilt.push(...companions);

        return { ...prev, packets: rebuilt };
      });
    };

    // Fetch fresh project data without resetting isLoading, and inject loading blocks
    // for activities that haven't arrived yet.
    let retryTimeoutId = null;
    const refreshWithLoadingBlocks = async (
      allDone,
      retryCount = 0,
      { secondaryOnly = false } = {}
    ) => {
      const thisGeneration = secondaryOnly ? ++secondaryFetchGeneration : ++primaryFetchGeneration;

      try {
        const response = await getProject(projectId);

        // Stale response — a newer fetch of the same type was started while this one was in-flight
        const currentGeneration = secondaryOnly ? secondaryFetchGeneration : primaryFetchGeneration;
        if (thisGeneration !== currentGeneration) return;

        const data = response.data;
        // Fallback when sessionStorage had no outline (opened fresh, e.g. from the MCP
        // connector link): derive the named placeholders from the outline on the response.
        if (!activityNames.length && data.outline) applyOutline(data.outline);
        const freshPackets = data.packets || [];

        // After the terminal done/error reconcile, a late in-flight interleave refresh must not
        // strand a loading placeholder — it renders as a skeleton the instant isStreaming flips,
        // because the sync feeding the rendered packet stops then. If this refresh's snapshot is
        // still incomplete, drop it and let the done reconcile + retry converge to the full set.
        // If it already has every block, fall through so its real blocks commit normally — never
        // dropping the final activity (the in-flight refresh, not the reconcile, may carry it).
        if (streamDone && !secondaryOnly && !allDone) {
          const streamingPacket = freshPackets.find((p) => p.packet.id === streamingPacketId);
          if ((streamingPacket?.packet?.blocks?.length || 0) < totalActivities) return;
        }

        // Release the primary from the generation registry on its OWN completion, not
        // at allDone — which on a run with secondaries fires only after they all
        // finish, and would keep a finished primary un-exportable until then.
        const freshPrimary = freshPackets.find((p) => p.packet.id === streamingPacketId);
        if (
          freshPrimary &&
          isPrimaryGenerationComplete(freshPrimary.packet, totalActivities, allDone)
        ) {
          setGeneratingPacketIds((ids) => withoutId(ids, streamingPacketId));
        }

        setProject(data.project);
        // Merge the snapshot into the CURRENT local state, read via the updater —
        // not a closure (the old secondaryOnly preserve read a stale `packets`
        // capture and could revert the primary to stream-start state). Local
        // block content wins by unique_id; the snapshot only ADDS newly-landed
        // blocks. This is what preserves teacher edits across every streaming
        // refetch: activity_ready, the done reconcile, and post-done retries.
        setPackets((prevPackets) => {
          const mergedPackets = mergePacketsPreservingLocal(prevPackets, freshPackets);

          // Primary packet: pad with loading blocks for activities not yet generated.
          // Secondary packets: keep as non-clickable spinner tabs until their
          // packet_ready event fires (tracked via readySecondaryPacketIds).
          const updatedPackets = mergedPackets.map((p) => {
            if (p.packet.id === streamingPacketId) {
              // Secondary-only refreshes never restructure the primary — the
              // merge above already carried local state over.
              if (secondaryOnly) return p;
              // Interleave real blocks with loading placeholders. Blocks from the
              // API carry _activity_index (set by the backend) so we know which
              // outline positions are filled vs still generating. Activities
              // complete out of order (DAG + FIRST_COMPLETED), so we can't assume
              // the first N blocks correspond to the first N outline positions.
              // totalActivities === 0: count unknown, so don't invent placeholders.
              if (
                totalActivities === 0 ||
                isPrimaryGenerationComplete(p.packet, totalActivities, allDone)
              ) {
                return p;
              }
              const realBlocks = p.packet.blocks || [];

              const filledIndices = new Set(
                realBlocks.map((b) => b._activity_index).filter((idx) => idx != null)
              );
              const displayBlocks = [];
              for (let i = 0; i < totalActivities; i++) {
                if (filledIndices.has(i)) {
                  // Real block exists for this position — find it
                  displayBlocks.push(realBlocks.find((b) => b._activity_index === i));
                } else {
                  const activityName = activityNames[i]?.name || `Activity ${i + 1}`;
                  displayBlocks.push(
                    createLoadingBlock({
                      title: activityName,
                      canonical_id: `loading_${i}`,
                      specified_id: `loading_${i}`,
                    })
                  );
                }
              }
              // Unindexed blocks (unsaved teacher/chat adds) ride after the outline.
              for (const b of realBlocks) {
                if (b._activity_index == null) displayBlocks.push(b);
              }
              return { ...p, packet: { ...p.packet, blocks: displayBlocks } };
            }

            // Secondary packets — non-clickable until packet_ready has fired
            if (
              !allDone &&
              p.packet.id !== streamingPacketId &&
              !readySecondaryPacketIds.has(p.packet.id)
            ) {
              return {
                ...p,
                packet: {
                  ...p.packet,
                  _isLoadingSecondary: true,
                },
              };
            }
            // Ready (or all done): clear a stale spinner flag the LOCAL copy may
            // carry — local packet fields win in the merge, so the flag no longer
            // clears via the snapshot passing through.
            if (p.packet.id !== streamingPacketId && p.packet._isLoadingSecondary) {
              const { _isLoadingSecondary: _spinner, ...packetRest } = p.packet;
              return { ...p, packet: packetRest };
            }

            return p;
          });

          // Inject placeholder tabs for secondary packets not yet in DB
          const expectedPacketCount = 1 + secondaryDeclarations.length;
          if (
            !allDone &&
            secondaryDeclarations.length > 0 &&
            updatedPackets.length < expectedPacketCount
          ) {
            const existingIds = new Set(updatedPackets.map((p) => p.packet.id));
            for (let si = 0; si < secondaryDeclarations.length; si++) {
              const sec = secondaryDeclarations[si];
              const placeholderId = `loading_secondary_${si}`;
              // Check if a real secondary packet already appeared in the project
              // (it won't have the placeholder ID, so check if we have more than primary)
              if (updatedPackets.length >= 1 + si + 1) continue;
              // Don't add duplicate placeholders
              if (existingIds.has(placeholderId)) continue;

              // Prefer the backend-supplied title — it derives from the primary's
              // packet_title, so the placeholder matches the name the finished
              // secondary will land with.
              const secTitle =
                sec.title ||
                (sec.type === 'translation' ? `${sec.language} Translation` : 'Companion Resource');

              updatedPackets.push({
                packet: {
                  id: placeholderId,
                  title: secTitle,
                  blocks: [],
                  orientation: sec.orientation,
                  _isLoadingSecondary: true,
                  _secondaryType: sec.type,
                  _secondaryIndex: si,
                },
                position: updatedPackets.length,
                premium_blocks: [],
              });
            }
          }

          // Re-apply any streamed images so this refetch can't revert a resolved
          // slot to a skeleton (the stale-snapshot race). Guarded/monotonic.
          return resolvedImagesRef.current.size
            ? produce(updatedPackets, (draft) =>
                applyImageOverlayToDraft(draft, resolvedImagesRef.current)
              )
            : updatedPackets;
        });
        setIsOwner(data.is_owner);
        setHasOwner(data.has_owner);
        setCanEdit(data.can_edit);
        setIsLoading(false);
        initialLoadDoneRef.current = true;

        // If stream is done but blocks are still incomplete (commit-to-read lag),
        // schedule a retry so we don't leave loading skeletons or empty tabs stranded.
        // Cap retries to avoid infinite polling when an activity errors.
        let needsRetry = false;
        if (allDone && retryCount < 3) {
          // Check primary
          if (totalActivities > 0) {
            const streamingPacket = freshPackets.find((p) => p.packet.id === streamingPacketId);
            const realBlockCount = streamingPacket?.packet?.blocks?.length || 0;
            if (realBlockCount < totalActivities) needsRetry = true;
          }

          // Check secondaries — if marked ready but blocks are empty (commit lag)
          for (const fp of freshPackets) {
            if (
              fp.packet.id !== streamingPacketId &&
              readySecondaryPacketIds.has(fp.packet.id) &&
              !fp.packet.blocks?.length
            ) {
              needsRetry = true;
              break;
            }
          }

          if (needsRetry) {
            retryTimeoutId = setTimeout(() => refreshWithLoadingBlocks(true, retryCount + 1), 1500);
          }
        }

        // Settled refresh (no retry pending, or retries exhausted) — rebuild the
        // outline from the actual packet contents.
        if (allDone && !needsRetry) {
          rebuildOutlineFromPackets(freshPackets);
        }
      } catch (err) {
        if (thisGeneration !== (secondaryOnly ? secondaryFetchGeneration : primaryFetchGeneration))
          return;
        console.error('Failed to refresh project during stream:', err);
        setIsLoading(false);
      }
    };

    // Inject initial loading blocks for activities 1..N
    refreshWithLoadingBlocks(false);

    // Track whether we've received the `done` event. When events arrive before
    // the resume connects, we may receive activity_ready + done in rapid succession.
    // The done handler does its own refresh, so we skip intermediate refreshes once done.
    let streamDone = false;

    // Track which secondary packets have received packet_ready (fully generated).
    // Until packet_ready fires, secondary tabs stay as non-clickable spinners.
    const readySecondaryPacketIds = new Set();
    // Secondaries this stream started that haven't finished or failed yet. Each goes Ready as it
    // starts (it's a spinner, with no pill, until it finishes), so it's still Ready if the teacher
    // leaves mid-generation; one that fails, or a failed stream's unfinished ones, are unmarked.
    const pendingSecondaryPacketIds = new Set();

    const finishStream = () => {
      setIsStreaming(false);
      try {
        sessionStorage.removeItem(`stream_outline_${streamId}`);
      } catch {
        /* ignore */
      }
      setSearchParamsRef.current(
        (prev) => {
          prev.delete('stream_id');
          prev.delete('last_event_id');
          prev.delete('total_activities');
          return prev;
        },
        { replace: true }
      );
    };

    // Deferred image resolved/failed → patch the matching slot(s) in memory by
    // image_id (a shared id spans the primary + its translation/relevel copies, so
    // we don't filter by packet). Recorded in the overlay so a concurrent refetch
    // can't revert it. No project refetch needed — the router already persisted it.
    const applyImageEvent = (event) => {
      const imageId = event.image_id;
      if (!imageId) return;
      if (event.type === 'image_ready') {
        const images = event.images || (event.image ? [event.image] : []);
        if (!images.length) return;
        resolvedImagesRef.current.set(imageId, { status: 'ready', images });
      } else {
        resolvedImagesRef.current.set(imageId, { status: 'failed' });
      }
      setPackets((prev) =>
        produce(prev, (draft) => applyImageOverlayToDraft(draft, resolvedImagesRef.current))
      );
    };

    const startResume = async () => {
      try {
        await resumeCreateStream(
          streamId,
          lastEventId,
          (event) => {
            const isSecondaryEvent = (event.packet_index || 0) > 0;
            if (event.type === 'image_ready' || event.type === 'image_error') {
              // Patch in place; no refetch (avoids reverting other in-flight images).
              applyImageEvent(event);
              return;
            }
            if (event.type === 'activity_ready' && !streamDone) {
              // Primary activity: full refresh with loading block interleaving.
              // Secondary activity: only update secondary tabs, preserve primary.
              refreshWithLoadingBlocks(false, 0, { secondaryOnly: isSecondaryEvent });
            } else if (event.type === 'packet_started' && !streamDone) {
              // Secondary packet created in DB — refresh tabs only
              setGeneratingPacketIds((ids) => withId(ids, event.packet_id));
              if (event.packet_id) {
                pendingSecondaryPacketIds.add(event.packet_id);
                markTabsReady([event.packet_id], READY_NEW);
              }
              refreshWithLoadingBlocks(false, 0, { secondaryOnly: true });
            } else if (event.type === 'packet_ready' && !streamDone) {
              // Secondary packet fully generated — mark as ready so the spinner
              // tab becomes a normal clickable tab on the next refresh.
              if (event.packet_id) {
                readySecondaryPacketIds.add(event.packet_id);
                setGeneratingPacketIds((ids) => withoutId(ids, event.packet_id));
                pendingSecondaryPacketIds.delete(event.packet_id);
              }
              refreshWithLoadingBlocks(false, 0, { secondaryOnly: true });
            } else if (event.type === 'packet_error') {
              // Secondary packet failed — refresh tabs only. It is no longer generating,
              // so release it like packet_ready does.
              console.warn('[SSE:ProjectResume] packet_error:', event.message);
              if (event.packet_id) {
                setGeneratingPacketIds((ids) => withoutId(ids, event.packet_id));
                pendingSecondaryPacketIds.delete(event.packet_id);
                forgetTabsReady([event.packet_id]);
              }
              refreshWithLoadingBlocks(false, 0, { secondaryOnly: true });
            } else if (event.type === 'done' || event.type === 'error') {
              streamDone = true;
              setGeneratingPacketIds(EMPTY_IDS);
              if (event.type === 'error' && pendingSecondaryPacketIds.size) {
                forgetTabsReady([...pendingSecondaryPacketIds]);
              }
              pendingSecondaryPacketIds.clear();
              // No need to mark the secondaries ready here: the refresh below passes
              // allDone, and readySecondaryPacketIds is only consulted in the
              // !allDone branch. (This used to walk `packets`, which after the
              // latch is always the effect-start value — i.e. empty — so it was
              // doing nothing anyway.)
              // Only refresh secondary tabs (to release any remaining spinners).
              // Don't touch the primary or already-clickable secondaries — the user
              // may have started editing.
              refreshWithLoadingBlocks(true, 0, { secondaryOnly: true }).then(() =>
                setTimeout(finishStream, 0)
              );
            }

            // Replay of outline_ready on resume — replace whole outline state.
            if (event.type === 'outline_ready' && event.outline) {
              setOutlineState(event.outline);
            }

            // Per-activity status: flip matching activity to ready.
            if (event.type === 'activity_ready') {
              const pIdx = event.packet_index ?? 0;
              const aIdx = event.index;
              setOutlineState(
                produce((draft) => {
                  if (!draft) return;
                  const packet = draft.packets.find((p) => p.packet_index === pIdx);
                  const activity = packet?.activities?.find((a) => a.index === aIdx);
                  if (activity) activity.status = 'ready';
                })
              );
            }

            // Whole-packet ready: flip the matching version (translation/releveled) to ready
            // inside the primary's versions array. Companions also fire packet_ready but their
            // per-activity status is already driven by activity_ready, so we ignore them here.
            if (event.type === 'packet_ready' && event.packet_index != null) {
              const pIdx = event.packet_index;
              setOutlineState(
                produce((draft) => {
                  if (!draft) return;
                  for (const packet of draft.packets) {
                    if (packet.role !== 'primary') continue;
                    const version = packet.versions?.find((v) => v.packet_index === pIdx);
                    if (version) version.status = 'ready';
                  }
                })
              );
            }
          },
          abortController.signal
        );
      } catch (err) {
        // AbortError means React StrictMode cleanup — don't touch state
        if (err.name === 'AbortError') return;
        console.error('Stream resume error:', err);
        streamDone = true;
        setGeneratingPacketIds(EMPTY_IDS);
        // Error recovery: full refresh to show whatever blocks exist in DB.
        // Don't use secondaryOnly — the stream failed, so we need to fetch
        // real data for ALL packets (primary may still have loading skeletons).
        await refreshWithLoadingBlocks(true);
        setTimeout(finishStream, 0);
      } finally {
        // Safety net: if stream closed without a done/error event (e.g. server crash,
        // proxy timeout), ensure we don't leave isStreaming stuck forever.
        if (!streamDone && !abortController.signal.aborted) {
          setGeneratingPacketIds(EMPTY_IDS);
          await refreshWithLoadingBlocks(true);
          setTimeout(finishStream, 0);
        }
      }
    };

    startResume();

    return () => {
      abortController.abort();
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      setGeneratingPacketIds(EMPTY_IDS);
    };
    // eslint-disable-next-line -- urlPacketId intentionally excluded: captured as
    // streamingPacketId at effect start so tab switches don't re-trigger the
    // streaming effect or target the wrong packet. searchParams and its setter are
    // excluded too — the session is latched (see streamLatch above), so an
    // unrelated param change must not tear this connection down. streamSession's
    // identity is stable for the life of one stream, so this runs once per stream.
  }, [projectId, streamSession]);

  // Backfill on load: if a packet still has pending image slots and we're NOT
  // live-streaming, the background generator likely died mid-image — resolve them
  // on demand. Deduped per packet so it fires once. The backend defers to any
  // active generation, so this is safe to call.
  useEffect(() => {
    if (isStreaming || isLoading) return;
    const pending = packets.filter(
      (p) =>
        p.packet?.id && !backfilledPacketIdsRef.current.has(p.packet.id) && hasPendingImages([p])
    );
    if (!pending.length) return;
    pending.forEach((p) => backfilledPacketIdsRef.current.add(p.packet.id));
    (async () => {
      const results = await Promise.all(
        pending.map((p) =>
          resolvePendingImages(p.packet.id)
            .then((r) => r?.blocks)
            .catch(() => null)
        )
      );
      // Harvest only the slot resolutions from the server's blocks — the teacher
      // may have edited while the calls were in flight, so replacing the blocks
      // wholesale would revert those edits. Same guarded/monotonic overlay the
      // live image_ready events use: only still-pending slots are touched.
      let harvested = false;
      for (const blocks of results) {
        if (blocks && harvestResolvedImageSlots(blocks, resolvedImagesRef.current)) {
          harvested = true;
        }
      }
      if (!harvested) return;
      setPackets((prev) =>
        produce(prev, (draft) => applyImageOverlayToDraft(draft, resolvedImagesRef.current))
      );
    })();
  }, [isStreaming, isLoading, packets]);

  // Refresh project data (called after chat adds a resource)
  // Returns the fresh packets array so callers can use it immediately
  // (React state won't reflect the update until the next render)
  const refreshProject = useCallback(
    async ({ forceRefreshIds = [] } = {}) => {
      if (!projectId) return null;

      try {
        const response = await getProject(projectId);
        const data = response.data;

        const freshPackets = data.packets || [];
        setProject(data.project);
        // Preserve the active packet's locally-synced data during refresh.
        // ProjectPacketContext syncs edits to ProjectContext via updatePacketData, but the API
        // may return stale data for the active packet (debounced save hasn't fired yet).
        // For all other packets, use fresh API data. Metadata fields (position, premium_blocks)
        // always come from the API regardless.
        // Exception: packets in forceRefreshIds were modified server-side (e.g. by agentic tools)
        // and should use fresh API data even if they're the active packet.
        const forceRefreshSet = new Set(forceRefreshIds);
        setPackets((prevPackets) => {
          const currentActiveId = activePacketIdRef.current;
          return freshPackets.map((fp) => {
            if (fp.packet.id === currentActiveId && !forceRefreshSet.has(currentActiveId)) {
              const existing = prevPackets.find((p) => p.packet.id === currentActiveId);
              if (existing) {
                return { ...fp, packet: existing.packet };
              }
            }
            return fp;
          });
        });
        setIsOwner(data.is_owner);
        setHasOwner(data.has_owner);
        setCanEdit(data.can_edit);
        return freshPackets;
      } catch (error) {
        console.error('Failed to refresh project:', error);
        return null;
      }
    },
    [projectId]
  );

  // Mark a packet tab as having unseen updates (the "new" pill with Ready tabs off). With them on,
  // a tab just added is new (the Ready pill) and one chat changed is changed (the dot).
  const markPacketUpdated = useCallback(
    (packetId, { added = false } = {}) => {
      setUpdatedPacketIds((prev) => new Set([...prev, packetId]));
      markTabsReady([packetId], added ? READY_NEW : READY_CHANGED);
    },
    [markTabsReady]
  );

  // Clear the unseen-update indicator for a packet tab
  const clearPacketUpdated = useCallback((packetId) => {
    setUpdatedPacketIds((prev) => {
      const next = new Set(prev);
      next.delete(packetId);
      return next;
    });
  }, []);

  // How a tab is marked until it's opened: 'new' (the Ready pill) for a resource just made in
  // this browser, 'changed' (the dot) for one chat changed while another tab was open, or null.
  const getPacketReadyKind = useCallback((packetId) => readyKind(readyTabs, packetId), [readyTabs]);

  // Switch to a different packet tab. `source` names what asked for the switch (tab_bar,
  // chat_suggestion, export_queue, ...) — many switches are programmatic, so without it
  // 'Tab Switched' can't tell a teacher finding a tab from the app moving them to one.
  const switchToPacket = useCallback(
    (packetId, { source } = {}) => {
      if (packetId === activePacketId) return;

      // Update URL (client-side navigation, no API reload). Carries the query string
      // minus the stream params — those describe the primary packet in the path, so
      // they must not follow us onto a secondary (see withoutStreamParams).
      navigate(
        packetUrlWithParams(projectId, packetId, withoutStreamParams(searchParamsRef.current)),
        { replace: true }
      );
      setActivePacketId(packetId);
      window.scrollTo({ top: 0 });

      // Clear "has updates" indicator when switching to this tab
      setUpdatedPacketIds((prev) => {
        if (!prev.has(packetId)) return prev;
        const next = new Set(prev);
        next.delete(packetId);
        return next;
      });

      Mixpanel.track('Tab Switched', {
        project_id: projectId,
        from_packet_id: activePacketId,
        to_packet_id: packetId,
        source,
        // The tab's mark under Ready tabs ('ready', 'dot' or 'none'), recorded in both arms of the
        // flag and on phones, whether or not it was shown.
        tab_mark: tabMarkLabel(getPacketReadyKind(packetId)),
      });
    },
    [projectId, activePacketId, navigate, getPacketReadyKind]
  );

  // Clone project and redirect (for non-owner edit attempts)
  //
  // `editedPacket` is the post-edit packet that usePacketState hands to onCloneAttempt
  // when a non-owner tries to save. Sending its editable fields along with the clone
  // means the edit lands in the copy's first INSERT, so the user doesn't have to retype
  // it after the redirect. Callers with no pending edit (banner, export, chat) pass
  // nothing and get a plain clone.
  const cloneProjectAndRedirect = useCallback(
    async (editedPacket = null, additionalParams = {}) => {
      if (isCloning || isOwner) return null;

      setIsCloning(true);
      try {
        // Keyed by the edited packet's own id (always the source packet, since a
        // non-owner only ever edits the project they're viewing) so the content and
        // the packet it applies to can't drift apart.
        const packetOverrides = editedPacket?.id
          ? { [editedPacket.id]: pickCloneOverrideFields(editedPacket) }
          : null;

        Mixpanel.track('Cloning Unowned Project', {
          source: 'project_edit',
          project_id: projectId,
          carried_edit: !!packetOverrides,
        });

        const response = await cloneProject(projectId, null, packetOverrides);
        const clonedProject = response.data;
        // A clone is a new project for the weekly quota, same as a workshop create.
        setDiffitUser(withProjectCounted);

        // Track successful clone per spec Section 12B
        Mixpanel.track('Project Cloned', {
          source_project_id: projectId,
          new_project_id: clonedProject.project.id,
          is_owner: false, // Clone-on-edit is always triggered by non-owners
          packet_count: clonedProject.packets?.length || 0,
          carried_edit: !!packetOverrides,
        });

        // Show success state briefly before redirect
        setShowSavedSuccess(true);

        // When an edit was carried, land on the tab it went into rather than the
        // project's first tab — otherwise the edit is preserved but out of sight, and
        // the user retypes it. Each clone points back at its source via releveled_from.
        const clonedPackets = clonedProject.packets || [];
        const editedCloneId = editedPacket?.id
          ? clonedPackets.find((p) => p.packet?.releveled_from === editedPacket.id)?.packet?.id
          : null;
        const targetPacketId = editedCloneId || clonedPackets[0]?.packet?.id;

        // Navigate to the cloned project
        setTimeout(() => {
          let targetUrl = targetPacketId
            ? `/project/${clonedProject.project.id}/${targetPacketId}`
            : `/project/${clonedProject.project.id}`;
          // The clone is definitionally not the packet being streamed, so the
          // stream params must not follow it into a different project.
          const redirectParams = withoutStreamParams(searchParams);
          for (const [key, value] of Object.entries(additionalParams)) {
            if (value == null) redirectParams.delete(key);
            else redirectParams.set(key, value);
          }
          const qs = redirectParams.toString();
          if (qs) targetUrl += '?' + qs;
          navigate(targetUrl, { replace: true });
        }, 800);

        // Returned so callers (e.g. export) can target the newly-cloned packet
        // without waiting for the 800ms redirect + project reload to resolve.
        return { packetId: targetPacketId };
      } catch (error) {
        console.error('Failed to clone project:', error);
        // The global quota modal is already up for this one.
        if (!isProjectQuotaExceeded(error)) {
          alert('Failed to save resource. Please try again later.');
        }
        setIsCloning(false);
        throw error;
      }
    },
    [isCloning, isOwner, projectId, navigate, searchParams, setDiffitUser]
  );

  // Debounced save for project updates - persists to server in background
  // Matches the pattern from PacketContext.debouncedSaveToServer
  const debouncedSaveProject = useCallback(
    debounce(
      async (fields) => {
        // don't attempt to save changes for non-owners or in version-view mode
        if (!isOwner || !projectId || versionId) return;
        try {
          await updateProject(projectId, fields);
        } catch (error) {
          console.error('Failed to save project:', error);
        }
      },
      SAVE_DEBOUNCE_MS,
      {
        leading: false, // don't save immediately
        trailing: true, // save after the changes have settled
      }
    ),
    [projectId, isOwner, versionId]
  );

  // Apply project updates with optimistic local state update + debounced server save
  // Matches the pattern from PacketContext.applyPacketUpdate
  const applyProjectUpdate = useCallback(
    (updates) => {
      if (!isOwner || versionId) return;

      // Immediate local state update (optimistic)
      if (updates.title !== undefined) {
        setProject((prev) => ({ ...prev, title: updates.title }));
      }
      if (updates.is_favorite !== undefined) {
        setProject((prev) => ({ ...prev, is_favorite: updates.is_favorite }));
      }
      if (updates.settings !== undefined) {
        setProject((prev) => ({ ...prev, settings: updates.settings }));
      }

      // Debounced save to server
      debouncedSaveProject(updates);
    },
    [isOwner, versionId, debouncedSaveProject]
  );

  // Update project title - uses unified applyProjectUpdate
  const updateProjectTitle = useCallback(
    (newTitle) => {
      applyProjectUpdate({ title: newTitle });
    },
    [applyProjectUpdate]
  );

  // Set Public/Private visibility for the whole project. Not routed through
  // applyProjectUpdate: that debounces into PUT /project/{id}, which records a
  // version-history entry, and visibility isn't a content edit. Optimistic so the control
  // moves under the teacher's finger, reverted if the write fails; resolves to the persisted
  // value so the UI can't drift from the server.
  const setProjectVisibility = useCallback(
    async (isPublic) => {
      if (!isOwner || !projectId || versionId) return null;
      const previous = project?.is_public;
      setProject((prev) => ({ ...prev, is_public: isPublic }));
      try {
        const response = await updateProjectVisibility(projectId, isPublic);
        const persisted = Boolean(response?.data?.is_public);
        setProject((prev) => ({ ...prev, is_public: persisted }));
        return persisted;
      } catch (error) {
        console.error('Failed to update project visibility:', error);
        setProject((prev) => ({ ...prev, is_public: previous }));
        return null;
      }
    },
    [isOwner, projectId, versionId, project?.is_public]
  );

  // Reorder packets with optimistic update
  // Updates local state immediately for instant UI feedback, then debounces server save
  const reorderPackets = useCallback(
    (newPacketsOrder) => {
      if (!isOwner || versionId) return;

      // Immediate local state update (optimistic) - updates position values
      const updatedPackets = newPacketsOrder.map((packet, index) => ({
        ...packet,
        position: index,
      }));
      setPackets(updatedPackets);

      // Debounced save to server using unified updateProject endpoint
      const tabOrder = newPacketsOrder.map((p) => p.packet.id);
      debouncedSaveProject({ tab_order: tabOrder });
    },
    [isOwner, versionId, debouncedSaveProject]
  );

  // Get the active packet data
  const getActivePacketData = useCallback(() => {
    if (!activePacketId || !packets.length) return null;
    const found = packets.find((p) => p.packet.id === activePacketId);
    return found ? found.packet : null;
  }, [activePacketId, packets]);

  // Get title for active packet
  const getActivePacketTitle = useCallback(() => {
    if (!activePacketId || !packets.length) return null;
    const found = packets.find((p) => p.packet.id === activePacketId);
    return found?.packet?.title || null;
  }, [activePacketId, packets]);

  // Get premium blocks for active packet
  const getActivePacketPremiumBlocks = useCallback(() => {
    if (!activePacketId || !packets.length) return [];
    const found = packets.find((p) => p.packet.id === activePacketId);
    return found?.premium_blocks || [];
  }, [activePacketId, packets]);

  // Get all packets in order
  const getAllPackets = useCallback(() => {
    return [...packets].sort((a, b) => a.position - b.position);
  }, [packets]);

  // Selectors
  const selectors = useMemo(
    () => ({
      getProject: () => project,
      getProjectId: () => projectId,
      getProjectTitle: () => project?.title,
      getPackets: () => packets,
      getAllPackets,
      getActivePacketId: () => activePacketId,
      getActivePacketData,
      getActivePacketTitle,
      getActivePacketPremiumBlocks,
      getIsOwner: () => isOwner,
      // Whether other teachers can discover this project's resources. NULL on the server
      // (projects predating the Public/Private control) reads as Private.
      getIsPublic: () => Boolean(project?.is_public),
      getHasOwner: () => hasOwner,
      getCanEdit: () => canEdit,
      getIsCloning: () => isCloning,
      getShowSavedSuccess: () => showSavedSuccess,
      getPacketCount: () => packets.length,
      isChatOpen: () => chatOpen,
      getPendingChatInput: () => pendingChatInput,
      // Remove resource confirmation modal
      getRemoveResourceConfirmation: () => removeResourceConfirmation,
      // Tab update indicators
      getUpdatedPacketIds: () => updatedPacketIds,
      getPacketReadyKind,
      // Whether a streaming create is still generating this packet
      isPacketGenerating: (packetId) => generatingPacketIds.has(packetId),
    }),
    [
      project,
      projectId,
      packets,
      getAllPackets,
      activePacketId,
      getActivePacketData,
      getActivePacketTitle,
      getActivePacketPremiumBlocks,
      isOwner,
      hasOwner,
      canEdit,
      isCloning,
      showSavedSuccess,
      chatOpen,
      pendingChatInput,
      removeResourceConfirmation,
      updatedPacketIds,
      getPacketReadyKind,
      generatingPacketIds,
    ]
  );

  // Mutators
  const mutators = useMemo(
    () => ({
      refreshProject,
      switchToPacket,
      cloneProjectAndRedirect,
      applyProjectUpdate,
      updateProjectTitle,
      setProjectVisibility,
      reorderPackets,
      hideSavedSuccess: () => setShowSavedSuccess(false),
      setChatOpen,
      setPendingChatInput,
      setActivePacketId: (packetId) => {
        navigate(
          packetUrlWithParams(projectId, packetId, withoutStreamParams(searchParamsRef.current)),
          { replace: true }
        );
        setActivePacketId(packetId);
      },
      // Remove resource confirmation modal
      setRemoveResourceConfirmation,
      clearRemoveResourceConfirmation: () => setRemoveResourceConfirmation(null),
      // Tab update indicators
      markPacketUpdated,
      clearPacketUpdated,
      // Update a packet's data in the packets array (used by ProjectPacketContext to sync state)
      updatePacketData: (packetId, updatedPacket) => {
        if (versionId) return;
        setPackets((prevPackets) =>
          prevPackets.map((p) => (p.packet.id === packetId ? { ...p, packet: updatedPacket } : p))
        );
      },
      // Replace the entire packets array from an authoritative project response.
      // Used by restore/undo/redo, which RECONCILE membership (packets can be added or
      // removed). A per-id merge like updatePacketData can't drop or add entries, so
      // removed tabs would otherwise linger until a full refresh. Mirrors loadProject.
      replacePackets: (freshPackets) => {
        if (versionId) return;
        setPackets(freshPackets || []);
      },
      // Update only the blocks for a specific packet (used by chat streaming for non-active packets)
      updatePacketBlocks: (packetId, blocks) => {
        if (versionId) return;
        setPackets((prevPackets) =>
          prevPackets.map((p) =>
            p.packet.id === packetId ? { ...p, packet: { ...p.packet, blocks } } : p
          )
        );
      },
      // Update blocks and packet-level settings for a non-active packet (used by packet_update SSE)
      updatePacketBlocksAndSettings: (packetId, blocks, settings) => {
        if (versionId) return;
        setPackets((prevPackets) =>
          prevPackets.map((p) =>
            p.packet.id === packetId
              ? {
                  ...p,
                  packet: {
                    ...p.packet,
                    blocks,
                    settings: { ...p.packet.settings, ...settings },
                  },
                }
              : p
          )
        );
      },
    }),
    [
      refreshProject,
      switchToPacket,
      cloneProjectAndRedirect,
      applyProjectUpdate,
      updateProjectTitle,
      setProjectVisibility,
      reorderPackets,
      markPacketUpdated,
      clearPacketUpdated,
      projectId,
      navigate,
      versionId,
    ]
  );

  const value = useMemo(
    () => ({
      ...selectors,
      ...mutators,
      isLoading,
      isStreaming,
      isSignedIn,
      outlineState,
      isViewingVersion: !!versionId,
      versionMeta,
      versionUnavailable,
      // Shared project-level undo/redo stack ref (null until the project loads)
      undoRedoStack,
    }),

    [
      selectors,
      mutators,
      isLoading,
      isStreaming,
      isSignedIn,
      versionId,
      versionMeta,
      versionUnavailable,
    ]
  );

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};

export default ProjectContext;
